import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, AuthSession, AuthStep } from '@/types/auth';

interface AuthState {
  session: AuthSession | null;
  user: User | null;
  isAuthenticated: boolean;
  isValidating: boolean;
  authStep: AuthStep;
  error: string | null;
  // Read-only demo mode. NEVER persisted, NEVER cross-tab synced, NEVER backed
  // by a sessionToken — see DEMO_TREASURY_DESIGN.md §3. The single source of
  // truth that widens ProtectedRoute and short-circuits the apiClient network
  // layer so no demo state can ever reach the relay.
  isDemo: boolean;
}

const initialState: AuthState = {
  session: null,
  user: null,
  isAuthenticated: false,
  isValidating: false,
  authStep: 'idle',
  error: null,
  isDemo: false,
};

// Load persisted state from localStorage (read-only on init).
//
// The session token lives in localStorage so a reload or a second tab stays
// signed in. That is only acceptable because the app loads no third-party
// script: everything is bundled and the production CSP (vite.csp.ts) pins
// script-src to 'self'. To keep exposure minimal, a token whose session has
// already expired is dropped from storage instead of being kept around.
const loadPersistedState = (): Partial<AuthState> => {
  try {
    const stored = localStorage.getItem('ssp-enterprise-auth');
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AuthState>;
      const expiresAt = parsed.session?.expiresAt ? Date.parse(parsed.session.expiresAt) : NaN;
      if (parsed.session && (!Number.isFinite(expiresAt) || expiresAt <= Date.now())) {
        localStorage.removeItem('ssp-enterprise-auth');
        return {};
      }
      return {
        session: parsed.session ?? null,
        user: parsed.user ?? null,
        isAuthenticated: parsed.isAuthenticated ?? false,
      };
    }
  } catch {
    // Ignore errors
  }
  return {};
};

const authSlice = createSlice({
  name: 'auth',
  initialState: { ...initialState, ...loadPersistedState() },
  reducers: {
    setSession: (state, action: PayloadAction<AuthSession>) => {
      state.session = action.payload;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.isValidating = false;
      state.authStep = 'authenticated';
      state.error = null;
    },
    setValidating: (state, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isValidating = false;
      if (state.session) {
        state.session.user = action.payload;
      }
    },
    setAuthStep: (state, action: PayloadAction<AuthStep>) => {
      state.authStep = action.payload;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.authStep = 'error';
    },
    logout: (state) => {
      state.session = null;
      state.user = null;
      state.isAuthenticated = false;
      state.isValidating = false;
      state.authStep = 'idle';
      state.error = null;
      state.isDemo = false;
    },
    // Enter the fully-faked, read-only demo. Deliberately clears any real auth:
    // demo and real sessions are mutually exclusive (DEMO_TREASURY_DESIGN.md §3).
    // No sessionToken is minted, so the apiClient Bearer interceptor has nothing
    // to attach and useSessionValidator never fires a real getSession().
    enterDemoMode: (state) => {
      state.isDemo = true;
      state.isAuthenticated = false;
      state.session = null;
      state.user = null;
      state.isValidating = false;
      state.authStep = 'idle';
      state.error = null;
    },
    exitDemoMode: (state) => {
      state.isDemo = false;
    },
    reset: (state) => {
      state.authStep = 'idle';
      state.error = null;
    },
  },
});

export const {
  setSession,
  setValidating,
  updateUser,
  setAuthStep,
  setError,
  logout,
  reset,
  enterDemoMode,
  exitDemoMode,
} = authSlice.actions;
export default authSlice.reducer;
